/**
 * Utility functions for session management and debugging
 */

/**
 * Perform a manual session refresh request
 * - Useful for debugging session refresh functionality
 * - Returns the server response
 */
type SessionRefreshResult = {
  code?: number
  error?: string
  [key: string]: unknown
}

export const manualSessionRefresh = async (): Promise<SessionRefreshResult> => {
  try {
    console.log("Attempting manual session refresh...")

    // Get the token from localStorage if available - original auth method
    let token = ""
    try {
      const auth = JSON.parse(localStorage.getItem("persist:msk:auth") || "{}")
      const user = JSON.parse(auth.user || "{}")
      token = user.token || ""
    } catch (e) {
      console.warn("Could not parse user data from localStorage", e)
    }

    // Set up headers
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    }

    // Add token if available
    if (token) {
      headers["Authorization"] = `Bearer ${token}`
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/saas/users/auth/refresh`,
      {
        method: "POST",
        credentials: "include",
        headers,
      },
    )

    const result = await response.json()
    console.log("Session refresh result:", result)

    // Update local storage timestamp if successful
    if (result.code === 200) {
      localStorage.setItem("session_last_refresh", Date.now().toString())
      console.log("Session refresh successful, timestamp updated")
    }

    return result
  } catch (error) {
    console.error("Manual session refresh error:", error)
    return { error: "Failed to refresh session" }
  }
}

/**
 * Check and log session information for debugging
 * - Use this in the browser console to debug session issues
 */
type SessionInfo = {
  timestamp: string
  lastRefresh: string
  sessionAge: string
  cookies: {
    visible: string
    parsed: Record<string, string>
    sessionId: string
    refreshToken: string
  }
  localStorage: {
    sessionLastRefresh: string | null
    sessionRefreshAttempt: string | null
  }
}

export const checkSessionInfo = (): SessionInfo => {
  const lastRefresh = localStorage.getItem("session_last_refresh")
  const lastRefreshDate = lastRefresh
    ? new Date(parseInt(lastRefresh, 10))
    : null
  const sessionAge = lastRefresh ? Date.now() - parseInt(lastRefresh, 10) : null

  // Get cookies (note: httpOnly cookies won't be visible)
  const cookies = document.cookie.split(";").reduce(
    (acc, cookie) => {
      const [name, value] = cookie.trim().split("=")
      if (name) acc[name] = value
      return acc
    },
    {} as Record<string, string>,
  )

  const sessionInfo = {
    timestamp: new Date().toISOString(),
    lastRefresh: lastRefreshDate?.toISOString() || "Never",
    sessionAge: sessionAge
      ? `${Math.floor(sessionAge / 1000 / 60)} minutes`
      : "Unknown",
    cookies: {
      visible: document.cookie || "(none visible)",
      parsed: cookies,
      sessionId: cookies["session-id"] || "(not visible - likely httpOnly)",
      refreshToken:
        cookies["refresh-token"] || "(not visible - likely httpOnly)",
    },
    localStorage: {
      sessionLastRefresh: lastRefresh,
      sessionRefreshAttempt: localStorage.getItem("session_refresh_attempt"),
    },
  }

  console.log("Session Info:", sessionInfo)
  return sessionInfo
}

// Expose utility functions to the window object for easy console access
if (typeof window !== "undefined") {
  type MeseeknaSessionUtils = {
    refresh: typeof manualSessionRefresh
    check: typeof checkSessionInfo
  }
  ;(
    window as Window & { meseeknaSession?: MeseeknaSessionUtils }
  ).meseeknaSession = {
    refresh: manualSessionRefresh,
    check: checkSessionInfo,
  }

  console.log(
    "Session debug utils available! Use 'meseeknaSession.check()' to check session info or 'meseeknaSession.refresh()' to manually trigger refresh",
  )
}
